// apps/client/src/ui/router.ts
import type { StringKey } from '@whiskerton/content';
import type React from 'react';
import type { Phase } from '../state/slices/core';
import { titleShowing, type TitleStep } from '../state/slices/title';
import type { GameStore } from '../state/types';
import { screenModules } from './screens.gen';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- augmented by every ui/screens/*.tsx file
export interface ScreenParams {}   // augmented: e.g. { pockets: undefined; letter: { mail: MailId } }
export type ScreenId = keyof ScreenParams & string;
export interface ScreenDef<K extends ScreenId = ScreenId> {
  id: K;
  layer: 'full' | 'panel' | 'overlay';
  component: (props: { params: ScreenParams[K]; close: () => void }) => React.ReactElement | null;
  menu?: { label: StringKey; order: number; mayorOnly?: boolean; visible?(store: GameStore): boolean };
}

const registry = new Map<string, ScreenDef>(
  (screenModules as readonly { screen: ScreenDef }[]).map((m) => [m.screen.id, m.screen]));

export function hasScreen(id: string): id is ScreenId { return registry.has(id); }
export function getScreen(id: string): ScreenDef | undefined { return registry.get(id); }
export function menuScreens(store: GameStore, isMayor: boolean): ScreenDef[] {
  return [...registry.values()]
    .filter((d) => d.menu && (!d.menu.mayorOnly || isMayor) && (d.menu.visible ? d.menu.visible(store) : true))
    .sort((a, b) => a.menu!.order - b.menu!.order);
}
const INVITE_PATH = /^\/invite\/([0-9A-HJKMNP-TV-Z]{10})\/?$/;
/** The full-screen base layer for a phase (contract change 10). Screens register later; App falls back to 'boot'. */
export function phaseScreen(phase: Phase, path: string): { id: string; params: unknown } {
  switch (phase) {
    case 'signedOut': return { id: 'signIn', params: undefined };
    case 'needsProfile': return { id: 'creator', params: undefined };
    case 'towns': {
      const m = INVITE_PATH.exec(path);
      return m ? { id: 'invite', params: { code: m[1] } } : { id: 'towns', params: undefined };
    }
    case 'inTown': return { id: 'hud', params: undefined };
    case 'updateRequired': return { id: 'update', params: undefined };   // revision 9 item 1 (M0-T18 registers it; App falls back to boot until then)
    default: return { id: 'boot', params: undefined };
  }
}
/** App's base layer (title screen, 2026-10-05): the title, at any path and in any phase, until Play now has run its swoop; then the
 *  phase's own screen. A town on its way in or on screen, and the update prompt its socket raises, are never covered (titleShowing). */
export function baseScreen(titleStep: TitleStep, phase: Phase, path: string): { id: string; params: unknown } {
  return titleShowing(titleStep, phase) ? { id: 'title', params: undefined } : phaseScreen(phase, path);
}
